import { Injectable, PLATFORM_ID, DOCUMENT, inject } from '@angular/core';
import { isPlatformBrowser } from '@angular/common';
import { NavigationEnd, Router } from '@angular/router';
import { filter } from 'rxjs/operators';

import { AnalyticsService } from './analytics.service';
import { PageClassification, classifyPath, normalizePath } from './page-type.model';
import { PageType, PageTypeSource } from './analytics.types';

/** Lo que la API acaba resolviendo para una URL que la ruta no determina. */
export type PageEntity =
  | { kind: 'deal'; category?: string; store?: string }
  | { kind: 'tag'; tagType: 'store' | 'category' | string; slug?: string }
  | { kind: 'not_found' }
  | { kind: 'error' }
  | { kind: 'ignored' };

interface PageSnapshot {
  page_path: string;
  page_location: string;
  page_referrer: string;
}

/**
 * Emite exactamente un page_view_spa por navegación.
 *
 * Tres cosas hacen que esto no sea un simple `subscribe` al router:
 *
 * 1. `initialNavigation: 'enabledBlocking'` completa la primera navegación antes
 *    de que exista AppComponent, así que el primer NavigationEnd no lo ve nadie:
 *    justo la página que trae el tráfico de Google. De ahí el catch-up de init().
 * 2. La página de error se pinta con `skipLocationChange`, así que la URL del
 *    router y la del navegador divergen. La clave de navegación se toma siempre
 *    de `location`; esa divergencia es precisamente la señal de "esto es un 404".
 * 3. Una ficha de chollo, una categoría y una tienda comparten la ruta `:id/`.
 *    El tipo real sólo lo sabe la API, así que la navegación queda pendiente y
 *    la resuelve product-category.page.ts.
 */
@Injectable({
  providedIn: 'root'
})
export class PageTrackerService {
  private platformId = inject(PLATFORM_ID);
  private document = inject<Document>(DOCUMENT);
  private router = inject(Router);
  private analytics = inject(AnalyticsService);


  /** Techo de espera de la llamada a `any/:id`. */
  private static readonly RESOLVE_TIMEOUT_MS = 1500;

  private started = false;
  private navSeq = 0;
  private emittedSeq = -1;
  private lastLocation = '';
  private pending: { seq: number; path: string; snapshot: PageSnapshot; timer: any } | null = null;
  /**
   * Resolución que llegó antes de que existiera la navegación pendiente. Pasa en
   * la carga directa de una ficha: la página pide `any/:id` durante la navegación
   * inicial, que con enabledBlocking termina antes de que exista AppComponent.
   */
  private earlyEntity: { path: string; entity: PageEntity } | null = null;

  init() {
    if (!isPlatformBrowser(this.platformId) || this.started) return;
    this.started = true;

    this.router.events
      .pipe(filter(event => event instanceof NavigationEnd))
      .subscribe(event => this.onNavigationEnd(event as NavigationEnd));

    // La navegación inicial ya ha terminado: se recupera a mano.
    if (this.router.navigated) this.onNavigationEnd(null);

    const win = this.document.defaultView;
    win?.addEventListener('pagehide', () => this.flushPending('unload'));
    this.document.addEventListener('visibilitychange', () => {
      if (this.document.visibilityState === 'hidden') this.flushPending('unload');
    });
  }

  /** Path de la navegación en curso, tal como lo ve el navegador. */
  currentPath(): string {
    return normalizePath(this.document.defaultView?.location.pathname || '/');
  }

  /**
   * Lo llama product-category.page.ts cuando la API dice qué era la URL. Se
   * descarta si no corresponde a la navegación pendiente: esa página no cancela
   * su petición, así que el callback de un chollo puede llegar con otro ya en
   * pantalla.
   */
  reportPageEntity(path: string, entity: PageEntity) {
    const normalized = normalizePath(path);

    if (!this.pending) {
      // Todavía no hay navegación abierta: se guarda para consumirla al abrirla.
      this.earlyEntity = { path: normalized, entity };
      return;
    }

    if (normalized !== this.pending.path) return;
    this.flush(this.pending.seq, this.classifyEntity(entity), 'api');
  }

  private onNavigationEnd(event: NavigationEnd | null) {
    const win = this.document.defaultView;
    if (!win) return;

    const browserPath = normalizePath(win.location.pathname);

    if (event) {
      const routerPath = normalizePath(event.urlAfterRedirects);

      // Navegación fantasma (skipLocationChange): no abre navegación nueva.
      if (routerPath !== browserPath) {
        if (routerPath === '/pagina-no-encontrada/' && this.pending) {
          this.flush(this.pending.seq, { type: 'not_found', deterministic: true }, 'api');
        }
        return;
      }
    }

    // La anterior no llegó a resolverse y el usuario ya se ha ido: se emite con
    // lo que se sabía, para no perder una página que sí se vio.
    if (this.pending) this.flushPending('superseded');

    const seq = ++this.navSeq;
    const snapshot: PageSnapshot = {
      page_path: win.location.pathname + win.location.search,
      page_location: this.document.URL,
      page_referrer: this.lastLocation || this.document.referrer || '',
    };

    const classification = classifyPath(browserPath);
    if (classification.deterministic) {
      this.earlyEntity = null;
      this.pending = { seq, path: browserPath, snapshot, timer: null };
      this.flush(seq, classification, 'path');
      return;
    }

    // El servidor ya resolvió esta URL para pintar los metadatos sociales.
    const hint = this.consumeServerHint(browserPath);
    if (hint) {
      this.pending = { seq, path: browserPath, snapshot, timer: null };
      this.flush(seq, hint, 'server');
      return;
    }

    this.pending = {
      seq,
      path: browserPath,
      snapshot,
      timer: setTimeout(
        () => this.flush(seq, { type: 'unknown', deterministic: false }, 'timeout'),
        PageTrackerService.RESOLVE_TIMEOUT_MS
      ),
    };

    const early = this.earlyEntity;
    this.earlyEntity = null;
    if (early && early.path === browserPath) {
      this.flush(seq, this.classifyEntity(early.entity), 'api');
    }
  }

  /**
   * De un solo uso y casada por ruta: tras una navegación del SPA de vuelta a la
   * misma URL, la pista ya no vale porque describe la carga inicial.
   */
  private consumeServerHint(browserPath: string): PageClassification | null {
    const win = this.document.defaultView;
    const hint = win?.__MC_PAGE__;
    if (!hint) return null;

    if (win) delete win.__MC_PAGE__;
    if (normalizePath(hint.path) !== browserPath) return null;

    return {
      type: hint.type as PageType,
      category: hint.category,
      store: hint.store,
      deterministic: true,
    };
  }

  private flushPending(source: PageTypeSource) {
    if (!this.pending) return;
    this.flush(this.pending.seq, { type: 'unknown', deterministic: false }, source);
  }

  private flush(seq: number, classification: PageClassification, source: PageTypeSource) {
    // Monótono creciente: garantiza un único push por navegación.
    if (seq <= this.emittedSeq) return;

    const pending = this.pending;
    if (!pending || pending.seq !== seq) return;

    this.emittedSeq = seq;
    if (pending.timer) clearTimeout(pending.timer);
    this.pending = null;
    this.lastLocation = pending.snapshot.page_location;

    this.analytics.trackPageView({
      ...pending.snapshot,
      // El título se lee al emitir, no al navegar: en las fichas lo escribe
      // MetadataService cuando responde la API.
      page_title: this.document.title,
      page_type: classification.type,
      page_category: classification.category,
      page_store: classification.store,
      page_type_source: source,
    });
  }

  private classifyEntity(entity: PageEntity): PageClassification {
    switch (entity.kind) {
      case 'deal':
        return { type: 'deal', category: entity.category, store: entity.store, deterministic: true };
      case 'tag': {
        const type: PageType = entity.tagType === 'store' ? 'store' : 'category';
        return {
          type,
          store: type === 'store' ? entity.slug : undefined,
          category: type === 'category' ? entity.slug : undefined,
          deterministic: true,
        };
      }
      case 'not_found':
        return { type: 'not_found', deterministic: true };
      case 'error':
        return { type: 'error', deterministic: true };
      default:
        return { type: 'unknown', deterministic: true };
    }
  }
}
